import { useEffect, useState } from "react";
import { readPromptParts, type PromptParts } from "../../../api/threads/promptPartsApi";
import { loadAfterTypingPause } from "../../../shared/loading/afterTypingPause";
import type { GivenValues } from "./PlannedInputsList";

/**
 * What an agent step's prompt fills itself, as its run would fill it now (F148): read once typing settles and again
 * when the log moves, with the values typed for its run. The last answer for the same step stays in view until the
 * next arrives, so typing does not blank the cards; a failed read keeps it. `null` for no step (`stepKey`), or before
 * the first answer. `values` is compared by content, so a new object with the same values reads nothing again.
 */
export function usePromptParts(threadId: string, stepKey: string | null, values: GivenValues, lastEntryNumber: number): PromptParts | null {
  const request = stepKey === null ? null : JSON.stringify([threadId, stepKey, values, lastEntryNumber]);
  const [answer, setAnswer] = useState<{ readonly threadId: string; readonly stepKey: string; readonly parts: PromptParts } | null>(null);
  useEffect(() => {
    if (request === null) return undefined;
    const [thread, step, given] = JSON.parse(request) as [string, string, GivenValues];
    return loadAfterTypingPause(
      (signal) => readPromptParts(thread, step, given, signal),
      (next) => { if (next.status === "loaded") setAnswer({ threadId: thread, stepKey: step, parts: next.value }); },
    );
  }, [request]);
  return answer !== null && answer.threadId === threadId && answer.stepKey === stepKey ? answer.parts : null;
}
